import { useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import VectorIcon from 'assets/VectorIcon.svg';
import addICon from 'assets/addIcon.svg';
import UploadIcon from 'assets/settings/components/UploadIcon';
import warningGrey from 'assets/warningGrey.svg';
import CustomDatePicker from 'common-ui/customDatePicker';
import Input from 'common-ui/input';
import Modal from 'common-ui/modal';
import Select from 'common-ui/select/Select';
import Switch from 'common-ui/switch';
import TextArea from 'common-ui/textArea';
import dayjs from 'dayjs';
import { getProducts } from 'features/newsales/newsalesActions';
import { selectProducts } from 'features/newsales/newsalesSlice';
import { formatDateTime } from 'utils/dateUtils';
import { notifyError } from 'utils/notifyConfig';
import * as Yup from 'yup';

import {
  ImagePreview,
  ImagePreviewContainer,
  MainContainer,
  SecondContainer,
  SwitchWrapper,
  UploadButton,
  UploadContainer,
  UploadHiddenInput,
  UploadIconWrapper,
  UploadLabel,
  UploadText,
} from '../LeadSourceModal/LeadSourceModal.styles';
import {
  AddFeatureButton,
  CharacterCounter,
  CounterText,
  CounterTextReq,
  ErrorText,
  ErrorText3,
  FeatureItem,
  FeaturesInput,
  FeaturesSection,
  FormContainer,
  GridEqualColumns,
  GridTwoColumns,
  RemoveFeatureButton,
} from './OfferModal.styles';

const CURRENCY_OPTIONS = [
  { value: 'AMD', label: 'AMD' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
];

const OfferModal = ({ mode = 'create', isOpen, onClose, onCreate, onUpdate, initialData }) => {
  const isEditMode = mode === 'edit';
  const dispatch = useDispatch();
  const products = useSelector(selectProducts);

  const [errors, setErrors] = useState({
    name: '',
    price: '',
    discountedPrice: '',
    image: '',
    productId: '',
  });

  const [touched, setTouched] = useState({
    name: false,
    price: false,
    productId: false,
    image: false,
  });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    discountedPrice: '',
    count: '',
    currency: 'AMD',
    productId: '',
    premiumFeatures: [''],
    image: null,
    enabled: true,
    startDate: '',
    endDate: '',
    salesHelpText: '',
  });
  const [imagePreview, setImagePreview] = useState(null);
  const hasImage = !!imagePreview;
  const isUploadDisabled = !!imagePreview || !!formData.image;
  const [hasNewImage, setHasNewImage] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState({
    value: 'AMD',
    label: 'AMD',
  });
  const fileInputRef = useRef(null);
  const ErrorWithIcon = ({ message }) => {
    if (!message) return null;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <img src={VectorIcon} alt="error" width={14} height={14} />
        <span>{message}</span>
      </div>
    );
  };

  useEffect(() => {
    if (isOpen) {
      dispatch(getProducts({ status: 'enabled', page: 1, limit: 100 }));
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (isOpen && isEditMode && initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        price: initialData.price || '',
        discountedPrice: initialData.discountedPrice || '',
        count: initialData.count || '',
        currency: initialData.currency || 'AMD',
        productId: initialData.productId || '',
        premiumFeatures: initialData.premiumFeatures ?? [''],
        image: null,
        enabled: initialData.enabled ?? true,
        salesHelpText: initialData.salesHelpText || '',
        startDate: initialData.startTime ? dayjs(initialData.startTime) : '',
        endDate: initialData.endTime ? dayjs(initialData.endTime) : '',
      });

      if (initialData.productId && products && products.length > 0) {
        const product = products.find((p) => p.id === initialData.productId);
        if (product) {
          setSelectedProduct({ value: product.id, label: product.name });
        }
      } else {
        setSelectedProduct(null);
      }

      const currencyValue = initialData.currency || 'AMD';
      setSelectedCurrency({
        value: currencyValue,
        label: currencyValue,
      });

      if (initialData.image) {
        const imageUrl = initialData.image.startsWith('http')
          ? initialData.image
          : `${process.env.REACT_APP_SALES_API_URL}${initialData.image}`;
        setImagePreview(imageUrl);
      } else {
        setImagePreview(null);
      }

      setHasNewImage(false);
    } else if (isOpen && !isEditMode) {
      setFormData({
        name: '',
        description: '',
        price: '',
        discountedPrice: '',
        count: '',
        currency: 'AMD',
        productId: '',
        premiumFeatures: [''],
        image: null,
        enabled: true,
      });
      setImagePreview(null);
      setHasNewImage(false);
      setSelectedProduct(null);
      setSelectedCurrency({ value: 'AMD', label: 'AMD' });
      setErrors({
        name: '',
        price: '',
        discountedPrice: '',
        image: '',
        productId: '',
      });
      setTouched({
        name: false,
        price: false,
        productId: false,
        image: false,
      });
    }
  }, [isOpen, isEditMode, initialData, products]);

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      discountedPrice: '',
      count: '',
      currency: 'AMD',
      productId: '',
      premiumFeatures: [],
      image: null,
      enabled: true,
    });
    setErrors({});
    setImagePreview(null);
    setHasNewImage(false);
    setSelectedProduct(null);
    setSelectedCurrency({ value: 'AMD', label: 'AMD' });
    setErrors({
      name: '',
      price: '',
      discountedPrice: '',
      image: '',
      productId: '',
    });
    setTouched({
      name: false,
      price: false,
      productId: false,
      image: false,
    });
    onClose();
  };

  const handleSubmit = async () => {
    const start = formData.startDate;
    const end = formData.endDate;

    // Mark all fields as touched
    setTouched({
      name: true,
      price: true,
      productId: true,
      image: true,
    });

    const validationErrors = {};
    if (!formData.name.trim()) {
      validationErrors.name = 'Name is required';
    } else if (formData.name.length > 50) {
      validationErrors.name = 'Max 50 characters';
    }

    if (!formData.price || Number(formData.price) <= 0) {
      validationErrors.price = 'Price is required';
    }
    if (!selectedProduct || !selectedProduct.value) {
      validationErrors.productId = 'Product is required';
    }
    if (Object.keys(validationErrors).length > 0) {
      console.log('validationErrors', validationErrors);
      setErrors(validationErrors);
      return;
    }
    const offerSchema = Yup.object({
      name: Yup.string().trim().required('Name is required').max(50, 'Max 50 characters'),
      price: Yup.string()
        .required('Price is required')
        .test('min-value', 'Price must be greater than 0', (value) => {
          if (!value) return false;
          return Number(value) > 0;
        })
        .max(15, 'Price must be shorter than or equal to 15 characters'),
      discountedPrice: Yup.string().nullable(),
      count: Yup.string().nullable(),
    });

    try {
      await offerSchema.validate(formData, {
        abortEarly: false,
        context: { isEditMode },
      });

      setErrors({});

      if (start && end && !isValidDateRange(start, end)) {
        setErrors((prev) => ({
          ...prev,
          endDate: 'End date must be greater than start date',
        }));
        return;
      }

      if (!selectedProduct?.value) {
        setErrors((prev) => ({
          ...prev,
          productId: 'Product is required',
        }));
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('description', formData.description.trim());
      formDataToSend.append('price', formData.price);
      formDataToSend.append('currency', selectedCurrency.value);
      if (formData.discountedPrice) {
        formDataToSend.append('discountedPrice', formData.discountedPrice);
      }

      if (formData.count) {
        formDataToSend.append('count', formData.count);
      }
      if (selectedProduct?.value) {
        formDataToSend.append('productId', selectedProduct.value);
      }
      formData.premiumFeatures
        .filter((f) => f.trim())
        .forEach((feature) => {
          formDataToSend.append('premiumFeatures', feature);
        });

      if (formData.startDate) {
        formDataToSend.append('startTime', formatDateTime(formData.startDate, true));
      }

      if (formData.endDate) {
        formDataToSend.append('endTime', formatDateTime(formData.endDate, true));
      }
      formDataToSend.append('enabled', formData.enabled ? 'true' : 'false');
      formDataToSend.append('salesHelpText', formData.salesHelpText?.trim() || '');

      formDataToSend.append('image', formData.image);

      if (isEditMode) {
        await onUpdate(initialData.id, formDataToSend);
      } else {
        await onCreate(formDataToSend);
      }

      setFormData({
        name: '',
        description: '',
        price: '',
        discountedPrice: '',
        count: '',
        currency: 'AMD',
        productId: '',
        premiumFeatures: [''],
        image: null,
        enabled: true,
        salesHelpText: '',
        startDate: '',
        endDate: '',
      });
      setErrors({});
      setImagePreview(null);
      setHasNewImage(false);
      setSelectedProduct(null);
      setSelectedCurrency({ value: 'AMD', label: 'AMD' });
    } catch (err) {
      if (err.inner) {
        const mappedErrors = {};
        err.inner.forEach((e) => {
          mappedErrors[e.path] = e.message;
        });
        setErrors(mappedErrors);
      }
    }
  };

  const isValidDateRange = (start, end) => {
    if (!start || !end) return true;
    return new Date(end) > new Date(start);
  };

  const handleInputChange = (field) => (e) => {
    let value = e.target.value;

    // Limit numeric fields to 15 characters
    if (field === 'price' || field === 'discountedPrice' || field === 'count') {
      if (value.length > 15) {
        return; // Prevent typing beyond 15 characters
      }
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Mark field as touched
    if (field === 'name' || field === 'price') {
      setTouched((prev) => ({ ...prev, [field]: true }));
    }

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];

      if (field === 'price') {
        const isValid = value.length <= 15 && Number(value) > 0;

        if (!value || Number(value) <= 0) {
          newErrors.price = 'Required and greater than 0';
        } else if (!isValid) {
          newErrors.price = 'price must be shorter than or equal to 15 characters';
        }
      }
      if (field === 'name') {
        if (!value.trim()) {
          newErrors.name = 'Name is required';
        } else if (value.length > 50) {
          newErrors.name = 'Max 50 characters';
        }
      }

      return newErrors;
    });
  };
  const handleDateChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSwitchToggle = () => {
    setFormData({ ...formData, enabled: !formData.enabled });
  };
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    setTouched((prev) => ({ ...prev, image: true }));

    if (file) {
      if (!file.type.startsWith('image/')) {
        setErrors((prev) => ({
          ...prev,
          image: 'Only image files are allowed',
        }));
        notifyError('Only image files are allowed.');
        e.target.value = '';
        return;
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        setErrors((prev) => ({
          ...prev,
          image: 'Image must be < 5MB',
        }));
        notifyError('Image size must be less than 5MB.');
        e.target.value = '';
        return;
      }

      setFormData({ ...formData, image: file });
      setHasNewImage(true);

      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.image;
        return newErrors;
      });

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleRemoveImage = () => {
    setFormData({ ...formData, image: null });
    setImagePreview(null);
    setHasNewImage(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Clear any image errors since image is optional
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.image;
      return newErrors;
    });
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFeatureChange = (index, value) => {
    const updated = [...formData.premiumFeatures];
    updated[index] = value;
    setFormData({ ...formData, premiumFeatures: updated });
  };

  const handleAddFeature = () => {
    if (formData.premiumFeatures.length >= 10) {
      notifyError('Maximum 10 features allowed');
      return;
    }

    setFormData({
      ...formData,
      premiumFeatures: [...formData.premiumFeatures, ''],
    });
  };

  const handleRemoveFeature = (index) => {
    if (index === 0) return;

    const updated = formData.premiumFeatures.filter((_, i) => i !== index);

    setFormData({
      ...formData,
      premiumFeatures: updated.length ? updated : [''],
    });
  };

  const handleProductChange = (selectedOption) => {
    setSelectedProduct(selectedOption);
    setTouched((prev) => ({ ...prev, productId: true }));

    // Clear product error if a product is selected
    if (selectedOption && selectedOption.value) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.productId;
        return newErrors;
      });
    } else {
      setErrors((prev) => ({
        ...prev,
        productId: 'Product is required',
      }));
    }
  };

  const handleCurrencyChange = (selectedOption) => {
    setSelectedCurrency(selectedOption);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      onOk={handleSubmit}
      title={isEditMode ? 'Edit Offer' : 'Create Offer'}
      footer
      closeIcon
      okText={isEditMode ? 'Update' : 'Create'}
      cancelText="Cancel"
      width={'870px'}
    >
      <FormContainer>
        <MainContainer>
          <div style={{ position: 'relative' }}>
            <Input
              label="Name"
              value={formData.name}
              onChange={handleInputChange('name')}
              placeholder="Enter offer name"
              required
              maxLength={50}
              error={touched.name && !!errors.name}
            />
            {touched.name && errors.name && (
              <ErrorText>
                <ErrorWithIcon message={errors.name} />
              </ErrorText>
            )}
          </div>

          <TextArea
            label={
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: '#000' }}>Description</span>
                <img src={warningGrey} alt="optional" width={14} height={14} />
              </span>
            }
            value={formData.description}
            onChange={handleInputChange('description')}
            placeholder="Enter description"
            rows={3}
            maxLength={250}
            resizeVertical={false}
            resizeHorizontal={false}
          />

          <TextArea
            label={
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: '#000' }}>Help Text</span>
                <img src={warningGrey} alt="optional" width={14} height={14} />
              </span>
            }
            value={formData.salesHelpText}
            onChange={handleInputChange('salesHelpText')}
            placeholder="Add any important information about this offer..."
            rows={3}
            maxLength={100}
            resizeVertical={false}
            resizeHorizontal={false}
          />
          <FeaturesSection>
            <UploadLabel
              style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <span style={{ color: '#000' }}>Premium Features</span>
              <img src={warningGrey} alt="optional" width={14} height={14} />
            </UploadLabel>

            {formData.premiumFeatures.map((feature, index) => (
              <FeatureItem key={index}>
                {index > 0 && (
                  <UploadLabel style={{ marginBottom: 4 }}>Feature {index + 1}</UploadLabel>
                )}

                <div style={{ position: 'relative', width: '100%' }}>
                  <FeaturesInput
                    value={feature}
                    onChange={(e) => handleFeatureChange(index, e.target.value)}
                    placeholder="Enter feature"
                    maxLength={100}
                    style={{ paddingRight: '30px' }}
                  />

                  {index > 0 && (
                    <RemoveFeatureButton onClick={() => handleRemoveFeature(index)}>
                      ×
                    </RemoveFeatureButton>
                  )}
                </div>

                <CharacterCounter>
                  <CounterText isMax={feature.length >= 100}>{feature.length}/100</CounterText>
                </CharacterCounter>
              </FeatureItem>
            ))}
            <AddFeatureButton
              onClick={handleAddFeature}
              style={{
                opacity: formData.premiumFeatures.length >= 10 ? 0.5 : 1,
                pointerEvents: formData.premiumFeatures.length >= 10 ? 'none' : 'auto',
              }}
            >
              <img src={addICon} alt="add" width={24} height={24} />
            </AddFeatureButton>
          </FeaturesSection>
        </MainContainer>
        <SecondContainer>
          {' '}
          <GridTwoColumns>
            <Select
              label="Currency"
              value={selectedCurrency}
              onChange={handleCurrencyChange}
              options={CURRENCY_OPTIONS}
              placeholder="Select currency"
              menuPlacement="auto"
              isSearchable={false}
            />

            <div style={{ position: 'relative' }}>
              <Input
                label="Price"
                type="number"
                value={formData.price}
                onChange={handleInputChange('price')}
                placeholder="12000"
                required
                error={touched.price && !!errors.price}
              />
              {touched.price && errors.price && (
                <ErrorText>
                  <ErrorWithIcon message={errors.price} />
                </ErrorText>
              )}
              <CharacterCounter>
                <CounterTextReq isMax={(formData.price?.toString() || '').length >= 15}>
                  {(formData.price?.toString() || '').length}/15 characters
                </CounterTextReq>
              </CharacterCounter>
            </div>
          </GridTwoColumns>
          <GridEqualColumns>
            <div style={{ position: 'relative' }}>
              <Input
                label={
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: '#000' }}>Discounted Price</span>
                    <img src={warningGrey} alt="optional" width={14} height={14} />
                  </span>
                }
                type="number"
                value={formData.discountedPrice}
                onChange={handleInputChange('discountedPrice')}
                placeholder="15000"
              />
              <CharacterCounter>
                <CounterText isMax={(formData.discountedPrice?.toString() || '').length >= 15}>
                  {(formData.discountedPrice?.toString() || '').length}/15 characters
                </CounterText>
              </CharacterCounter>
            </div>

            <div style={{ position: 'relative' }}>
              <Input
                label={
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: '#000' }}>Count</span>
                    <img src={warningGrey} alt="optional" width={14} height={14} />
                  </span>
                }
                type="number"
                value={formData.count}
                onChange={handleInputChange('count')}
                placeholder="100"
              />
              <CharacterCounter>
                <CounterText isMax={(formData.count?.toString() || '').length >= 15}>
                  {(formData.count?.toString() || '').length}/15 characters
                </CounterText>
              </CharacterCounter>
            </div>
          </GridEqualColumns>
          <div style={{ position: 'relative' }}>
            <Select
              label="Product"
              req={true}
              value={selectedProduct}
              onChange={handleProductChange}
              options={
                products && products.length > 0
                  ? products.map((product) => ({
                      value: product.id,
                      label: product.name,
                    }))
                  : []
              }
              placeholder="Select a product"
              isClearable
              menuPlacement="auto"
              noOptionsMessage="No products available"
            />
            {errors.productId && (
              <ErrorText>
                <ErrorWithIcon message={errors.productId} />
              </ErrorText>
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <UploadContainer>
              <UploadLabel style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: '#000' }}>Image</span>
                <img src={warningGrey} alt="optional" width={14} height={14} />
              </UploadLabel>

              <UploadHiddenInput
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />

              <UploadButton
                type="button"
                onClick={handleUploadClick}
                disabled={isUploadDisabled}
                style={{
                  opacity: isUploadDisabled ? 0.5 : 1,
                  cursor: isUploadDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                <UploadIconWrapper>
                  <UploadIcon size={32} color="#296BCD" />
                </UploadIconWrapper>

                <UploadText>
                  {hasImage ? 'Image uploaded' : 'Click to upload image'}
                </UploadText>
              </UploadButton>

              {hasImage && (
                <ImagePreviewContainer
                  style={{
                    position: 'relative',
                    marginTop: 12,
                    background: 'transparent',
                    padding: 0,
                    border: 'none',
                    width: 120,
                    height: 80,
                  }}
                >
                  <ImagePreview
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />

                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    style={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      background: '#fff',
                      border: '1px solid #ddd',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 16,
                      fontWeight: 'bold',
                      color: '#000',
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </ImagePreviewContainer>
              )}
            </UploadContainer>

            {touched.image && errors.image && (
              <ErrorText3>
                <ErrorWithIcon message={errors.image} />
              </ErrorText3>
            )}
          </div>
          {/* NEXT SECTION */}
          <GridTwoColumns></GridTwoColumns>
          <GridTwoColumns>
            <CustomDatePicker
              label="Start Date"
              placeholder="Select start date"
              value={formData.startDate}
              minDate={dayjs().startOf('day')}
              maxDate={formData.endDate ? dayjs(formData.endDate) : null}
              onChange={(e) => handleDateChange('startDate', e)}
            />
            <CustomDatePicker
              label="End Date"
              placeholder="Select end date"
              value={formData.endDate}
              onChange={(e) => handleDateChange('endDate', e)}
              disableFuture={false}
              minDate={formData.startDate ? dayjs(formData.startDate) : null}
            />
          </GridTwoColumns>
          <SwitchWrapper>
            <Switch isOn={formData.enabled} onToggle={handleSwitchToggle} disabled={false} />
            {formData.enabled ? 'Enabled' : 'Disabled'}
          </SwitchWrapper>
        </SecondContainer>
      </FormContainer>
    </Modal>
  );
};

export default OfferModal;
